import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import ReactApexChart from "react-apexcharts"; // Import ApexCharts
import Link from "antd/es/typography/Link";
import ConsumptionVsConsumerCluster from "./ConsumptionVsConsumerCluster";
import ConsumptionVsConsumerCluster2 from "./ConsumptionVsConsumerCluster2";
const CELL_WIDTH = 165; // Set a fixed width for the table cells

const ConsumptionDecreasingCluster = () => {
  const [apexChartData, setApexChartData] = useState({});
  const [secondTableData, setSecondTableData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [summaryData, setSummaryData] = useState(null);
  // Sorting state

  useEffect(() => {
    // Parse all_data.csv for the table data
    fetch("/all_data.csv")
    .then((response) => response.text()) // Get the CSV file as plain text
    .then((csvText) => {
      Papa.parse(csvText, {
        header: true,
        worker:true,
        complete: (result) => {
          console.log("fetch", result);
          if (result.data && result.data.length > 0) {
            // Filter the data to include only rows for account ID 7662453172
  
            // Second table: Filter rows where any 'out_*' column has a value of 7
            const outData = result.data.filter((row) => {
              return Object.keys(row).some(
                (key) => key.startsWith("out") && row[key] === "2"
              );
            });
  
            // Map 'out_*' data and remove 'CONS_' prefix from column headers
            const mappedOutData = outData.map((row) => {
              const cleanedRow = {};
              let clusterValue = ""; // To store the corresponding cluster value
  
              Object.entries(row).forEach(([key, value]) => {
                // Capture 'out_*' column values to display in the 'Cluster' column
                if (key.startsWith("out")) {
                  clusterValue += ` ${value} `;
                }
                // Remove 'CONS_' prefix from other columns
                cleanedRow[
                  key.startsWith("CONS_") ? key.replace("CONS_", "") : key
                ] = value;
              });
  
              // Modify the 'dist' column to be shown as 'deviation' with values multiplied by 100
              if (cleanedRow["deviation_percentage"]) {
                cleanedRow["deviation"] = (
                  parseFloat(cleanedRow["deviation_percentage"]) 
                ).toFixed(2);
              } else {
                cleanedRow["deviation"] = ""; // Add an empty string if 'dist' is undefined
              }
  
              return { ...cleanedRow, Cluster: clusterValue.trim() }; // Add the concatenated cluster values
            });
            const limitedData = mappedOutData.slice(0, 1000);
            setSecondTableData(limitedData); // Set the filtered and cleaned data for the second table
          }
        },
      });
    })
    .catch((error) => {
      console.error("Error fetching the CSV file:", error);
    });

    // Parse merged_data_sum_normalized.csv for the graph data
    Papa.parse("/merged_data_sum_normalized.csv", {
      download: true,
      header: true,
      complete: (result) => {
        if (result.data && result.data.length > 0) {
          // Filter the rows where 'out_*' columns have a value of 7
          const filteredGraphData = result.data.filter((row) =>
            Object.keys(row).some(
              (key) => key.startsWith("out") && row[key] === "2"
            )
          );

          if (filteredGraphData.length > 0) {
            // Extract and sort 'CONS_*' columns for the x-axis labels
            const consColumns = Object.keys(filteredGraphData[0])
              .filter((key) => key.startsWith("CONS"))
              .sort(); // Sort the columns in ascending order

            // Prepare x-axis labels by removing 'CONS_' prefix
            const labels = consColumns.map((col) => col.replace("CONS_", ""));

            // Prepare y-axis data using corresponding values of 'CONS_*' columns
            const values = consColumns.map((col) =>
              parseFloat(parseFloat(filteredGraphData[0][col]).toFixed(1))
            );

            // Set data for ApexCharts
            setApexChartData({
              options: {
                chart: {
                  id: "apexchart-example",
                  toolbar: {
                    show: false, // Disable extra toolbar for simplicity
                  },
                  title: {
                    text: "Cluster 1 Consumption",
                    align: "center",
                    style: {
                      fontSize: "18px",
                    },
                  },
                },
                xaxis: {
                  categories: labels,
                },
                markers: {
                  size: 4, // Add markers for each point on the graph
                  colors: ["#FF4560"],
                },
              },
              series: [
                {
                  name: "Cluster 1",
                  data: values,
                },
              ],
            });
          }
        }
      },
    });

    Papa.parse("/all_data_summary.csv", {
      download: true,
      header: true,
      complete: (result) => {
        console.log("Parsed all_data_summary.csv:", result); // Debugging statement

        if (result.data && result.data.length > 0) {
          // Log the entire result to check if data is correct
          console.log("CSV Rows:", result.data);
          
          // Find rows where 'out' column is equal to 7
          const summaryRow = result.data.find((row) => row.cluster === "2");
          console.log("Found Summary Row (out = 7):", summaryRow); // Debugging statement

          if (summaryRow) {
            const consumers = summaryRow["no of consumer"];
            const percentage = summaryRow["percentage"];
            console.log("Consumers:", consumers, "Percentage:", percentage); // Debugging statement
            setSummaryData({ consumers, percentage });
          } else {
            console.error("No row with 'out' = 7 found.");
          }
        } else {
          console.error("No data in all_data_summary.csv or file format issue.");
        }
      },
    });
  }, []);

  // Sort function for secondTableData
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    const sortedData = [...secondTableData].sort((a, b) => {
      const aValue = Number(a[key]);
      const bValue = Number(b[key]);
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setSecondTableData(sortedData);
  };
  

  // Extract table headers for the second table, ensuring the first column is 'Cluster'
  const tableHeaders =
    secondTableData.length > 0
      ? [
          "Cluster",
          "AC_ID",
          "Deviation Percentage",
          ...Object.keys(secondTableData[0]).filter(
            (key) =>
              key !== "AC_ID" &&
              key !== "Cluster" &&
              key !== "deviation_percentage" &&
              key !== "out" &&
              key !== "dist" &&
              key !== "DIV" &&
              key !== "SUPPLY_TYPE" &&
              key !== "CATEGORY" &&
              key !== "GOVT_TAG" &&
              key !== "PROCESS" &&
              key !== "NAME" &&
              key !== "ADDRESS" &&
              key !== "SITE_PROCESS" &&
              key !== "CONTACT_DEMAND_KW" &&
              key !== "Unnamed: 0" &&
              key !== "deviation" &&
              key.trim() !== ""
          ),
        ]
      : [];

  return (
    <div className="main-wrapper">
      <div className="page-wrapper">
      <div >
      <h2 style={{ textAlign: "center" ,marginBottom:"6px" ,marginTop:"6px"}}>Consumption Decreasing Cluster</h2>

          {/* Line Graph using ApexCharts */}
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div  style={{marginInline:"30px"}}>
                 <b>Cluster 2 </b> 
                  {apexChartData.series ? (
                    <ReactApexChart
                      options={apexChartData.options}
                      series={apexChartData.series}
                      type="line"
                      height={265}
                      width={400}
                    />
                  ) : (
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* First Table: Showing sorted 'CONS_*' data for Account ID 7662453172 */}
            <div className="col-lg-6">
              <div className="card" style={{ minHeight: "300px" }}>
                <div className="card-header">
                <h2 className=" mb-0" style={{ textAlign: "center" ,marginBottom:"4px" ,marginTop:"4px"}}>Insight Summary</h2>
                </div>
                <div style={{marginInline:"10px"}}>
                  <ul >
                  {summaryData ? (
                  <>
                    <li style={{marginBottom:"5px"}}>&#10148; Total Number of Consumers: {summaryData.consumers}</li>
                    <li style={{marginBottom:"5px"}}>&#10148; Percentage of Consumers: {summaryData.percentage}%</li>
                  </>
                ) : (
                  <li>Loading data...</li>
                )}
                    <li style={{marginBottom:"5px"}}>
                   
                      &#10148; If consumption declines unnaturally or sharply,
                      it could signal tampering with the meter to report lower
                      usage.
                    </li>
                    <li style={{marginBottom:"5px"}}>
                      &#10148; Declining consumption could be a result of
                      deliberate meter manipulation or fraudulent reporting.
                    </li>
                  
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Second Table: Filter rows where 'out_*' = 7 */}
          <div className="card">
          
            <div className="card-body">
            <h2 className=" mb-1">Consumption Table</h2>
              <div className="table-responsive"  style={{
        maxHeight: "800px", // Set a maximum height for the table container
        overflowY: "auto",   // Enable vertical scrolling within the table container
        overflowX: "auto",   // Enable horizontal scrolling if needed
      }}>
                {secondTableData.length > 0 ? (
                  <table
                    className="table table-bordered mb-0"
                    style={{ width: "100%", tableLayout: "fixed" }}
                  >
                    <thead>
                      <tr>
                        {tableHeaders.map((header, index) => (
                          <th
                            key={index}
                            style={{
                              width: `${CELL_WIDTH}px`,
                              whiteSpace: "nowrap",
                              backgroundColor: "#3651FB",
                              color: "whitesmoke",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleSort(
                                header === "Deviation Percentage" ? "deviation" : header
                              )
                            } // Handle sorting on click
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {secondTableData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {tableHeaders.map((header, colIndex) => (
                            <td
                              key={colIndex}
                              style={{
                                width: `${CELL_WIDTH}px`,
                                whiteSpace: "nowrap",
                              }}
                            >
                         {header === "Deviation Percentage" && row["deviation"]
                                ? `${row["deviation"]}%`
                                : <>{header === "AC_ID" && row["AC_ID"] ?
                                <Link
                                  // className="dropdown-item"
                                  to="#"
                                  data-bs-toggle="modal"
                                  // data-bs-backdrop="static"
                                  aria-labelledby="delete"
                                  aria-hidden="true"
                                  // data-bs-target="#edit_leave"
                                  data-bs-target="#delete"
                                  
                                
                                >
                                  
                                    {row["AC_ID"]}
                                </Link>:row[header]}</> }
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConsumptionVsConsumerCluster2/>
    </div>
  );
};

export default ConsumptionDecreasingCluster;
